import React, { useMemo } from 'react';
import CustomCheckbox from '@/components/CustomCheckbox';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import TerminalPausedBadge, {
  TerminalPausedDetailLine,
} from './TerminalPausedBadge';
import { isTerminalPauseActive } from './terminalPauseUtils';

const legacyTerminalPriorityOptions = [
  { label: 'None', value: 'NONE' },
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
  { label: 'Exclude', value: 'EXCLUDE' },
];

const TerminalLegacyPrioritySelect = ({ value, onChange }) => {
  const normalized =
    value != null && String(value).trim() !== ''
      ? String(value).toUpperCase()
      : 'NONE';
  return (
    <CustomSelectEx
      key={normalized || 'empty'}
      options={legacyTerminalPriorityOptions}
      value={
        legacyTerminalPriorityOptions.find((opt) => opt.value === normalized) ||
        null
      }
      onChange={(opt) => onChange(opt?.value ?? 'NONE')}
      placeholder="Select Priority"
      width="100%"
      minWidth="135px"
      dropdown={true}
    />
  );
};

function terminalDisplayName(key, entry) {
  if (entry && typeof entry === 'object') {
    const s = entry.slug;
    if (s != null && String(s).trim() !== '') return String(s).trim();
  }
  const k = key != null ? String(key).trim() : '';
  if (k !== '') return k;
  if (entry && typeof entry === 'object') {
    const n = entry.name;
    if (n != null && String(n).trim() !== '') return String(n).trim();
  }
  return key;
}

const TerminalPreferences = ({
  terminalPrefs,
  setTerminalPrefs,
  rulesTerminalDesign = false,
}) => {
  const sortedKeys = useMemo(() => {
    const keys = Object.keys(terminalPrefs || {});
    if (!rulesTerminalDesign) {
      return [...keys].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
      );
    }
    return [...keys].sort((a, b) =>
      terminalDisplayName(a, terminalPrefs[a]).localeCompare(
        terminalDisplayName(b, terminalPrefs[b]),
        undefined,
        { sensitivity: 'base' }
      )
    );
  }, [terminalPrefs, rulesTerminalDesign]);

  if (sortedKeys.length === 0) {
    return (
      <div className="terminal-section">
        <p className="terminal-empty-hint m-0 text-sm text-[#717784]">
          No terminals in preferences yet.
        </p>
      </div>
    );
  }

  if (!rulesTerminalDesign) {
    return (
      <div className="terminal-section">
        <div className="terminal-grid">
          {sortedKeys.map((terminal) => {
            const entry =
              terminalPrefs[terminal] &&
              typeof terminalPrefs[terminal] === 'object'
                ? terminalPrefs[terminal]
                : {};
            const p =
              entry.priority != null && String(entry.priority).trim() !== ''
                ? String(entry.priority)
                : 'NONE';
            const paused = isTerminalPauseActive(entry.timeout);
            return (
              <div
                key={terminal}
                className={paused ? 'terminal-box terminal-box-paused' : 'terminal-box'}>
                <div className="terminal-label-row">
                  <span className="terminal-label">{terminal}</span>
                  <TerminalPausedBadge
                    terminalKey={terminal}
                    entry={entry}
                    compact
                  />
                </div>
                <TerminalLegacyPrioritySelect
                  value={p}
                  onChange={(val) =>
                    setTerminalPrefs((prev) => ({
                      ...prev,
                      [terminal]: {
                        ...(prev[terminal] && typeof prev[terminal] === 'object'
                          ? prev[terminal]
                          : {}),
                        priority: String(val || 'NONE').toUpperCase(),
                      },
                    }))
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="terminal-section">
      <div className="terminal-list">
        {sortedKeys.map((key) => {
          const entry =
            terminalPrefs[key] && typeof terminalPrefs[key] === 'object'
              ? terminalPrefs[key]
              : {};
          const excluded =
            String(entry.priority ?? '').toUpperCase() === 'EXCLUDE';
          const label = terminalDisplayName(key, entry);
          const paused = isTerminalPauseActive(entry.timeout);
          return (
            <div
              key={key}
              className={paused ? 'terminal-row terminal-row-paused' : 'terminal-row'}>
              <div className="terminal-row-head">
                <CustomCheckbox
                  compact
                  size="small"
                  checked={excluded}
                  onChange={(e) =>
                    setTerminalPrefs((prev) => ({
                      ...prev,
                      [key]: {
                        ...(prev[key] && typeof prev[key] === 'object'
                          ? prev[key]
                          : {}),
                        priority: e.target.checked ? 'EXCLUDE' : 'NONE',
                      },
                    }))
                  }
                  label={label}
                  labelClass={
                    excluded
                      ? 'terminal-label terminal-label-excluded pl-1'
                      : 'terminal-label pl-1'
                  }
                />
                <TerminalPausedBadge terminalKey={key} entry={entry} compact />
              </div>
              {paused ? (
                <TerminalPausedDetailLine
                  entry={entry}
                  terminalKey={key}
                  row
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TerminalPreferences;
